import React from "react";
import { Download, ImageIcon, Loader2, PencilLine, RefreshCw, X } from "lucide-react";
import { formatNumber } from "./imageCreationService";

const PREVIEW_BOX = {
  "16:9": "w-full aspect-video",
  "4:3": "w-full max-w-lg aspect-[4/3]",
  "1:1": "w-full max-w-sm aspect-square",
  "3:4": "h-80 max-w-full aspect-[3/4]",
  "9:16": "h-80 max-w-full aspect-[9/16]",
};

function UsageLine({ usage }) {
  if (!usage || usage.totalTokens === null || usage.totalTokens === undefined) {
    return <span className="text-xs text-gray-500">Usage wasn't reported for this image.</span>;
  }
  const tool = usage.imageTool;
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-xs tabular-nums text-gray-600">
      <span>
        Tokens <b className="text-gray-900">{formatNumber(usage.totalTokens)}</b>
      </span>
      <span>Input {formatNumber(usage.inputTokens)}</span>
      <span>Output {formatNumber(usage.outputTokens)}</span>
      {tool && tool.totalTokens !== null && <span>Image tool {formatNumber(tool.totalTokens)}</span>}
    </div>
  );
}

export default function GeneratedVersions({
  versions,
  selected,
  onSelect,
  generating,
  aspectRatio,
  onDownload,
  onRegenerate,
  onEditPrompt,
  canRegenerate,
  onClear,
}) {
  const ratio = generating || !selected ? aspectRatio : selected.aspectRatio;
  const btn =
    "inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-gray-900">Generated Image</span>
        {versions.length > 0 && !generating && onClear && (
          <button
            type="button"
            onClick={onClear}
            aria-label="Remove the generated image"
            title="Remove the generated image"
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <X size={15} />
          </button>
        )}
      </div>

      <div className="grid min-h-[200px] place-items-center rounded-xl bg-gray-100 p-2">
        <div className={`relative mx-auto overflow-hidden rounded-lg bg-gray-200 ${PREVIEW_BOX[ratio] || PREVIEW_BOX["16:9"]}`}>
          {selected && !generating ? (
            <img src={selected.imageDataUrl} alt={selected.title || "Generated menu"} className="h-full w-full object-contain" />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-4 text-center text-sm text-gray-500">
              {generating ? (
                <>
                  <Loader2 size={26} className="animate-spin text-indigo-600" />
                  <span className="font-medium text-gray-700">Creating your image...</span>
                  <span className="text-xs">This usually takes under a minute.</span>
                </>
              ) : (
                <>
                  <ImageIcon size={24} />
                  Your edited image will appear here
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {selected && !generating && (
        <>
          <div className="flex flex-col gap-1">
            {selected.title && <p className="text-sm font-semibold text-gray-900">{selected.title}</p>}
            <UsageLine usage={selected.usage} />
            <span className="font-mono text-xs tabular-nums text-gray-400">
              {selected.width}×{selected.height} · {selected.aspectRatio}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button" className={btn} onClick={onDownload}>
              <Download size={14} /> Download
            </button>
            <button type="button" className={btn} onClick={onRegenerate} disabled={!canRegenerate}>
              <RefreshCw size={14} /> Regenerate
            </button>
            <button type="button" className={btn} onClick={onEditPrompt}>
              <PencilLine size={14} /> Edit Prompt
            </button>
          </div>
        </>
      )}

      {versions.length > 1 && (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Versions</span>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {versions.map((v, i) => {
              const active = selected && v.key === selected.key;
              return (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => onSelect(v.key)}
                  disabled={generating}
                  className={`flex w-32 flex-none flex-col overflow-hidden rounded-lg border text-left ${
                    active ? "border-indigo-500 ring-2 ring-indigo-200" : "border-gray-200 hover:border-gray-400"
                  }`}
                >
                  <img src={v.imageDataUrl} alt="" className="aspect-video w-full bg-gray-100 object-contain" />
                  <span className="px-2 py-1 text-xs font-medium text-gray-800">Version {versions.length - i}</span>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-gray-400">Versions are kept while this page is open. Download any you want to keep.</p>
        </div>
      )}
    </div>
  );
}
